import { useState, type FormEvent } from 'react';
import {
  DataGrid,
  dayBoundary,
  useGridState,
  type GridColumn,
} from '../../components/data-grid/DataGrid';
import { Link, Navigate } from 'react-router-dom';
import {
  ERROR_LOG_SOURCES,
  type ErrorLog,
  type ErrorLogSource,
  type ErrorLogStatus,
} from '@neuroedge/shared';
import { Button, Card, Modal, PageState, TextField, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import {
  useArchiveErrorLogs,
  useErrorLog,
  useErrorLogCounts,
  useErrorLogs,
  type ErrorLogFilters,
  usePurgeErrorLogs,
  useUpdateErrorLogStatus,
} from '../../api/error-logs';

/**
 * Platform admin "Error Logs": the in-house error tracker's review surface.
 * Every `error`/`fatal` log line from the API, cron workers and background
 * jobs, plus browser crashes reported by the web app, lands here (see
 * apps/api/src/shared/logging/error-capture.ts). Triage: Resolve (fixed /
 * acknowledged) -> Archive (hidden from the default view) -> Purge
 * (permanently deleted, archived entries only).
 */

const TABS: { value: ErrorLogStatus | 'ALL'; label: string }[] = [
  { value: 'OPEN', label: 'Open' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'ARCHIVED', label: 'Archived' },
  { value: 'ALL', label: 'All (not archived)' },
];

const SOURCE_LABELS: Record<ErrorLogSource, string> = {
  API: 'API request',
  WORKER: 'Cron worker',
  BACKGROUND_JOB: 'Background job',
  PROCESS: 'Process',
  WEB: 'Web (browser)',
};

const muted = { color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' } as const;

function when(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function ErrorLogsPage() {
  const { isPlatformAdmin } = useAuth();
  const [tab, setTab] = useState<ErrorLogStatus | 'ALL'>('OPEN');
  const [grid, setGrid] = useGridState({ sortBy: 'createdAt', sortOrder: 'desc' });
  const [fingerprint, setFingerprint] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [bulkDialog, setBulkDialog] = useState<'archive' | 'purge' | null>(null);

  const f = grid.filters;
  const filters: ErrorLogFilters = {
    status: tab === 'ALL' ? undefined : tab,
    source: (f.source as ErrorLogSource) || undefined,
    level: (f.level as 'ERROR' | 'FATAL') || undefined,
    search: f.message?.trim() || undefined,
    route: f.route?.trim() || undefined,
    tenant: f.tenantName?.trim() || undefined,
    fingerprint: fingerprint ?? undefined,
    createdFrom: dayBoundary(f.createdAtFrom, false),
    createdTo: dayBoundary(f.createdAtTo, true),
    sortBy: grid.sortBy as ErrorLogFilters['sortBy'],
    sortOrder: grid.sortOrder,
    page: grid.page,
    pageSize: grid.pageSize,
  };
  const logs = useErrorLogs(filters);
  const counts = useErrorLogCounts(filters);

  if (!isPlatformAdmin) return <Navigate to="/" replace />;

  function showSimilar(fp: string) {
    setFingerprint(fp);
    setTab('ALL');
    setGrid({ ...grid, page: 1 });
    setSelectedId(null);
  }

  const columns: GridColumn<ErrorLog>[] = [
    {
      key: 'createdAt',
      header: 'When',
      sortable: true,
      filter: { type: 'dateRange' },
      render: (e) => <span style={{ whiteSpace: 'nowrap' }}>{when(e.createdAt)}</span>,
    },
    {
      key: 'level',
      header: 'Level',
      sortable: true,
      filter: {
        type: 'select',
        options: [
          { value: 'ERROR', label: 'Error' },
          { value: 'FATAL', label: 'Fatal' },
        ],
      },
      render: (e) => (
        <span
          className="ne-badge"
          style={e.level === 'FATAL' ? { color: 'var(--color-danger)' } : undefined}
        >
          {e.level}
        </span>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      sortable: true,
      filter: {
        type: 'select',
        options: ERROR_LOG_SOURCES.map((s) => ({ value: s, label: SOURCE_LABELS[s] })),
      },
      render: (e) => SOURCE_LABELS[e.source],
    },
    {
      key: 'message',
      header: 'Error',
      filter: { type: 'text', placeholder: 'Message, code or reference' },
      render: (e) => (
        <button
          type="button"
          onClick={() => setSelectedId(e.id)}
          style={{
            all: 'unset',
            cursor: 'pointer',
            color: 'var(--color-primary)',
            fontWeight: 600,
            overflowWrap: 'anywhere',
          }}
        >
          {e.errorName ? `${e.errorName}: ` : ''}
          {e.message}
        </button>
      ),
    },
    {
      key: 'route',
      header: 'Route',
      filter: { type: 'text', placeholder: 'e.g. POST /quotations' },
      render: (e) =>
        e.route ? (
          <span style={muted}>
            {e.httpMethod ? `${e.httpMethod} ` : ''}
            {e.route}
            {e.statusCode ? ` · ${e.statusCode}` : ''}
          </span>
        ) : (
          '—'
        ),
    },
    {
      key: 'tenantName',
      header: 'Tenant',
      filter: { type: 'text' },
      render: (e) => e.tenantName ?? 'No tenant',
    },
    {
      key: 'occurrences',
      header: 'Seen',
      sortable: true,
      align: 'right',
      render: (e) => `${e.occurrences}×`,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
        }}
      >
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Error Logs</h1>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Button variant="secondary" onClick={() => setBulkDialog('archive')}>
            Archive old…
          </Button>
          <Button variant="secondary" onClick={() => setBulkDialog('purge')}>
            Delete archived…
          </Button>
        </div>
      </div>

      {fingerprint ? (
        <p style={{ margin: 0 }}>
          Showing every occurrence of one error.{' '}
          <Button
            variant="secondary"
            onClick={() => {
              setFingerprint(null);
              setGrid({ ...grid, page: 1 });
            }}
          >
            Clear
          </Button>
        </p>
      ) : null}

      <Card>
        <div role="tablist" aria-label="Error status" className="ne-tabs">
          {TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={tab === t.value}
              className="ne-tabs__tab"
              onClick={() => {
                setTab(t.value);
                setGrid({ ...grid, page: 1 });
              }}
            >
              {t.label}
              {counts.data ? (
                <span className="ne-tabs__count">
                  {t.value === 'ALL' ? counts.data.all : counts.data[t.value]}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 'var(--space-3)' }}>
          {logs.isError ? (
            <PageState
              kind="error"
              message={logs.error.message}
              onRetry={() => void logs.refetch()}
            />
          ) : (
            <DataGrid
              columns={columns}
              rows={logs.data?.items ?? []}
              rowKey={(e) => e.id}
              rowLabel={(e) => e.message}
              total={logs.data?.meta.total ?? 0}
              state={grid}
              onStateChange={setGrid}
              loading={logs.isFetching}
              emptyMessage="No errors here."
            />
          )}
        </div>
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/admin/dashboard">Admin dashboard</Link> · <Link to="/">← Back to dashboard</Link>
      </p>

      {selectedId ? (
        <ErrorLogDetailModal
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onShowSimilar={showSimilar}
        />
      ) : null}
      {bulkDialog ? <BulkDialog kind={bulkDialog} onClose={() => setBulkDialog(null)} /> : null}
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string | number | null }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={muted}>{label}</div>
      <div style={{ overflowWrap: 'anywhere' }}>{value ?? '—'}</div>
    </div>
  );
}

const preStyle = {
  margin: 0,
  padding: 'var(--space-3)',
  background: 'var(--color-surface-muted)',
  borderRadius: 'var(--radius-md)',
  fontSize: 12,
  maxHeight: 280,
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
} as const;

function ErrorLogDetailModal({
  id,
  onClose,
  onShowSimilar,
}: {
  id: string;
  onClose: () => void;
  onShowSimilar: (fingerprint: string) => void;
}) {
  const detail = useErrorLog(id);
  const update = useUpdateErrorLogStatus();
  const toast = useToast();
  const [applyToSimilar, setApplyToSimilar] = useState(true);

  async function setStatus(status: ErrorLogStatus) {
    try {
      const { affected } = await update.mutateAsync({
        id,
        input: { status, applyToSimilar: applyToSimilar && (detail.data?.occurrences ?? 1) > 1 },
      });
      toast.success(
        `${affected} ${affected === 1 ? 'entry' : 'entries'} marked ${status.toLowerCase()}.`,
      );
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update this entry.');
    }
  }

  return (
    <Modal title="Error details" onClose={onClose} wide>
      {detail.isPending ? (
        <PageState kind="loading" />
      ) : detail.isError ? (
        <PageState kind="error" message={detail.error.message} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
          <p style={{ margin: 0, fontWeight: 600, overflowWrap: 'anywhere' }}>
            {detail.data.errorName ? `${detail.data.errorName}: ` : ''}
            {detail.data.message}
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: 'var(--space-3)',
            }}
          >
            <DetailField label="When" value={when(detail.data.createdAt)} />
            <DetailField label="Source" value={SOURCE_LABELS[detail.data.source]} />
            <DetailField label="Level" value={detail.data.level} />
            <DetailField label="Status" value={detail.data.status} />
            <DetailField label="Tenant" value={detail.data.tenantName ?? detail.data.tenantId} />
            <DetailField label="User id" value={detail.data.userId} />
            <DetailField
              label="Request"
              value={
                detail.data.route
                  ? `${detail.data.httpMethod ?? ''} ${detail.data.route}`.trim()
                  : null
              }
            />
            <DetailField label="HTTP status" value={detail.data.statusCode} />
            <DetailField label="Error code" value={detail.data.errorCode} />
            <DetailField label="Reference (correlation id)" value={detail.data.correlationId} />
            <DetailField label="Occurrences" value={detail.data.occurrences} />
          </div>

          {detail.data.occurrences > 1 ? (
            <div>
              <Button variant="secondary" onClick={() => onShowSimilar(detail.data.fingerprint)}>
                Show all {detail.data.occurrences} occurrences
              </Button>
            </div>
          ) : null}

          <div>
            <div style={muted}>Stack trace</div>
            <pre style={preStyle}>{detail.data.stack ?? 'No stack trace captured.'}</pre>
          </div>
          {Object.keys(detail.data.context).length > 0 ? (
            <div>
              <div style={muted}>Context</div>
              <pre style={preStyle}>{JSON.stringify(detail.data.context, null, 2)}</pre>
            </div>
          ) : null}

          {detail.data.occurrences > 1 ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <input
                type="checkbox"
                checked={applyToSimilar}
                onChange={(e) => setApplyToSimilar(e.target.checked)}
              />
              Apply to all {detail.data.occurrences} occurrences of this error
            </label>
          ) : null}
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {detail.data.status !== 'RESOLVED' ? (
              <Button disabled={update.isPending} onClick={() => void setStatus('RESOLVED')}>
                Mark resolved
              </Button>
            ) : null}
            {detail.data.status !== 'ARCHIVED' ? (
              <Button
                variant="secondary"
                disabled={update.isPending}
                onClick={() => void setStatus('ARCHIVED')}
              >
                Archive
              </Button>
            ) : null}
            {detail.data.status !== 'OPEN' ? (
              <Button
                variant="secondary"
                disabled={update.isPending}
                onClick={() => void setStatus('OPEN')}
              >
                Reopen
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </Modal>
  );
}

function BulkDialog({ kind, onClose }: { kind: 'archive' | 'purge'; onClose: () => void }) {
  const archive = useArchiveErrorLogs();
  const purge = usePurgeErrorLogs();
  const toast = useToast();
  const [days, setDays] = useState(kind === 'archive' ? '30' : '90');
  const [includeOpen, setIncludeOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const minDays = kind === 'archive' ? 0 : 1;
  const pending = archive.isPending || purge.isPending;

  function validate(): Record<string, string> {
    const n = Number(days);
    if (days.trim() === '' || !Number.isInteger(n) || n < minDays || n > 3650) {
      return { olderThanDays: `Enter a whole number of days between ${minDays} and 3650.` };
    }
    return {};
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    setFormError(null);
    if (Object.keys(errors).length) return;
    try {
      const olderThanDays = Number(days);
      const { affected } =
        kind === 'archive'
          ? await archive.mutateAsync({ olderThanDays, includeOpen })
          : await purge.mutateAsync({ olderThanDays });
      toast.success(
        `${affected} ${affected === 1 ? 'entry' : 'entries'} ${kind === 'archive' ? 'archived' : 'deleted'}.`,
      );
      onClose();
    } catch (err) {
      const apiFieldErrors = fieldErrorsFromApiError(err);
      setFieldErrors(apiFieldErrors);
      if (!Object.keys(apiFieldErrors).length) {
        setFormError(err instanceof Error ? err.message : 'Could not complete this action.');
      }
    }
  }

  return (
    <Modal
      title={kind === 'archive' ? 'Archive old errors' : 'Delete archived errors'}
      onClose={onClose}
    >
      <form onSubmit={submit} noValidate style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <p style={{ margin: 0, ...muted }}>
          {kind === 'archive'
            ? 'Hides resolved errors older than the cutoff from the default view. They stay searchable under "Archived" until deleted.'
            : 'Permanently deletes archived errors older than the cutoff. This cannot be undone.'}
        </p>
        <TextField
          label="Older than (days)"
          type="number"
          inputMode="numeric"
          min={minDays}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          error={fieldErrors.olderThanDays}
        />
        {kind === 'archive' ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <input
              type="checkbox"
              checked={includeOpen}
              onChange={(e) => setIncludeOpen(e.target.checked)}
            />
            Also archive errors that are still open
          </label>
        ) : null}
        {formError ? <p style={{ margin: 0, color: 'var(--color-danger)' }}>{formError}</p> : null}
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={pending}>
            {kind === 'archive' ? 'Archive' : 'Delete permanently'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
